import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import FormLayout from '../components/FormLayout.js';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { Input } from '../components/ui/input.tsx';
import { LoadingButton } from '../components/ui/loading-button.tsx';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import { useCreateChild } from '../api/student/studentQueries.js';
import { useGetParents } from '../api/parent/parentQueries.js';
import { createTemplateNotification } from '../api/notifications/staffNotificationApi';
import { toast } from 'react-toastify';

function AddStudent() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: addStudent } = useCreateChild();
  const {
    data: parentData,
    isPending: parentLoading,
    isError,
  } = useGetParents();

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      parentEmail: '',
      dob: '',
    },
  });

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      const studentData = {
        ...data,
        dob: new Date(data.dob).toISOString(),
      };

      await addStudent(studentData);

      // Show success toast and create notification
      await createTemplateNotification('studentAdded');

      navigate('/student/list');
    } catch (error) {
      // Handle error
      console.error('Error adding student:', error);
      toast.error('Failed to add student');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout title="Enter Student Details" showBackButton={false}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dob"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of Birth</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="parentEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Parent</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={parentLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          parentLoading
                            ? 'Loading parents...'
                            : 'Select a parent'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {parentLoading ? (
                        <div className="flex items-center justify-center p-4">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : isError ? (
                        <div className="p-4 text-red-500 text-sm">
                          Failed to load parents
                        </div>
                      ) : parentData === undefined ||
                        parentData.length === 0 ? (
                        <div className="p-4 text-gray-500 text-sm">
                          No parents found
                        </div>
                      ) : (
                        parentData &&
                        parentData.map((parent) => {
                          // Debug log each parent
                          console.log('Rendering parent:', parent);
                          return (
                            <SelectItem
                              key={parent.parentId || Math.random()}
                              value={parent.email || ''}
                            >
                              {parent.firstName || ''} {parent.lastName || ''}
                              {parent.email ? ` (${parent.email})` : ''}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <LoadingButton
            type="submit"
            loading={isLoading}
            className="w-full text-white"
            style={{
              backgroundColor: '#8444e4',
              height: '53px',
            }}
          >
            Add New Student
          </LoadingButton>

          <div className="flex items-center justify-center my-1">
            <span className="text-gray-500">or</span>
          </div>

          <LoadingButton
            type="button"
            className="w-full"
            style={{
              backgroundColor: '#f3ebfc',
              color: '#8f58e8',
              height: '53px',
            }}
            onClick={() => navigate('/parent/new')}
            disabled={isLoading}
          >
            Create New Parent Profile
          </LoadingButton>
        </form>
      </Form>
    </FormLayout>
  );
}

export default AddStudent;
